/**
 * @file ImportFileFormatDropdown.js
 * @description A dropdown component for selecting an import file format.
 */

import React, { useState, useEffect } from "react";
import {
  getImportFileFormats,
  getOperators,
} from "../../../services/importService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

const ImportFileFormatDropdown = ({
  className,
  inlineLabel = true,
  selectedFileFormat,
  onFileFormatChange,
  required = false,
}) => {
  const [fileFormats, setFileFormats] = useState([]);
  const [defaultLabel, setDefaultLabel] = useState("Select File Format");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchImportFileFormats = async () => {
      try {
        const data = await getImportFileFormats();
        setFileFormats(data);

        const matchingFileFormat =
          selectedFileFormat &&
          data.find((format) => format.id === selectedFileFormat.id);
        setDefaultLabel(
          matchingFileFormat ? matchingFileFormat.name : "Select File Format"
        );
      } catch (error) {
        console.error("Could not fetch import file formats:", error);
        Toast.error(getText('msg_3e34be834f01'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchImportFileFormats();
  }, []);

  useEffect(() => {
    if (selectedFileFormat && fileFormats.length > 0) {
      const matchingFileFormat = fileFormats.find(
        (format) => format.id === selectedFileFormat.id
      );
      setDefaultLabel(
        matchingFileFormat ? matchingFileFormat.name : "Select File Format"
      );
    } else {
      setDefaultLabel("Select File Format");
    }
  }, [selectedFileFormat, fileFormats]);

  const fileFormatOptions = [
    { value: null, label: getText('msg_671552ae366f') },
    ...fileFormats.map((format) => ({
      value: { id: format.id, name: format.name },
      label: format.name,
    })),
  ];

  return (
    <div
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className}`}
    >
      <label htmlFor="importFileFormat">
        {getText('msg_5dac61882da9')}{required && <span className="text-danger">*</span> }
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          defaultValue={defaultLabel}
          options={fileFormatOptions}
          onChange={(value) => onFileFormatChange(value)}
          value={selectedFileFormat}
          disabled={fileFormats.length === 0 || isLoading}
        />
      </LoadingWrapper>
    </div>
  );
};

export default ImportFileFormatDropdown;
